import React from "react";
import { Link, useLocation } from "react-router-dom";
import { EventixData } from "../../Data/WebsiteData";

const Footer = () => {
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white text-gray-600 pt-16 pb-12 border-t border-gray-200 relative overflow-hidden">
      {/* Subtle Gold Glow Effect */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>

      <div className="w-[95%] md:w-[90%] mx-auto relative z-10">
        {/* Main Grid Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-16">

          {/* Column 1: Brand Info */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-1 animate-fade-in-up" style={{ animationDelay: '0ms' }}>
            <div className="flex items-center gap-3 mb-2">
              <img
                src={EventixData?.brand?.logo}
                alt="Eventix-logo"
                className="h-[60px] sm:h-[70px] md:h-[90px] object-contain -ml-4 -mt-6"
              />
            </div>
            <p className="text-[#0B1F33] text-md font-bold font-serif italic mb-2 tracking-wide leading-relaxed max-w-sm">
              {EventixData?.brand?.tagline}
            </p>
            <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
              {EventixData?.brand?.description}
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div className="animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            <h4 className="font-bold text-[#0B1F33] mb-6 text-lg relative inline-block">
              Navigation
              <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-[#D4AF37]"></span>
            </h4>
            <ul className="space-y-3 text-sm text-gray-600">
              {[
                { label: 'Home', path: "/" },
                { label: 'About Us', path: "/about" },
                { label: 'Our Gallery', path: "/gallery" },
                { label: 'Contact Us', path: "/contact" }, // Contact link added
              ].map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    className="hover:text-[#D4AF37] transition-all duration-300 flex items-center gap-2 hover:translate-x-1"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#D4AF37]"></span>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Event Coverage & Hours */}
          <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <h4 className="font-bold text-[#0B1F33] mb-6 text-lg relative inline-block uppercase tracking-wider">
              Coverage
              <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-[#D4AF37]"></span>
            </h4>
            <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 border-l-4 border-l-[#D4AF37] shadow-sm">
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] uppercase text-gray-400 font-bold tracking-widest mb-1">Office Hours</p>
                  <p className="text-sm text-[#0B1F33] font-semibold">Mon — Sat: 09:00 AM - 07:00 PM</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 leading-relaxed italic">
                    "Available for Events across India & Nepal"
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Column 4: Contact & Socials */}
          <div className="animate-fade-in-up" style={{ animationDelay: '300ms' }}>
            <h4 className="font-bold text-[#0B1F33] mb-6 text-lg relative inline-block">
              Get In Touch
              <span className="absolute bottom-0 left-0 w-1/2 h-0.5 bg-[#D4AF37]"></span>
            </h4>
            <div className="space-y-4">
              <a href={`mailto:${EventixData?.contact?.supportEmail}`} className="flex items-center gap-3 group">
                <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-[#D4AF37] transition-colors">
                  <svg className="w-4 h-4 text-[#0B1F33]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <span className="text-sm group-hover:text-[#D4AF37] transition-colors">{EventixData?.contact?.supportEmail}</span>
              </a>
              <a href={`tel:${EventixData?.contact?.phone}`} className="flex items-center gap-3 group">
                <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-[#D4AF37] transition-colors">
                  <svg className="w-4 h-4 text-[#0B1F33]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
                <span className="text-sm group-hover:text-[#D4AF37] transition-colors">{EventixData?.contact?.phone}</span>
              </a>

              <div className="flex gap-3 mt-6">
                {[
                  { name: 'facebook', path: "M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z", href: EventixData?.socials?.facebook },
                  { name: 'instagram', path: "M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.451 2.53c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z", href: EventixData?.socials?.instagram },
                  { name: 'linkedin', path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z", href: EventixData?.socials?.linkedin },
                  { name: 'youtube', path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z", href: EventixData?.socials?.youtube}
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-[#D4AF37] text-gray-500 hover:text-[#0B1F33] transition-all duration-300 hover:scale-110 shadow-sm"
                    aria-label={social.name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d={social.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-gray-200 text-center animate-fade-in-up" style={{ animationDelay: '400ms' }}>
          <p className="text-sm text-gray-400 font-medium tracking-wide">
            © {currentYear} <span className="text-[#0B1F33] font-bold">Eventix</span> — Crafting moments, Creating memories. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;